import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import Badge from "../../../../../components/elements/Badge";
import {
  taxonomyAddEdit,
  taxonomyDelete,
  taxonomyDetails,
  taxonomyList,
  taxonomyStatusChange,
} from "../../../../../services/taxonomyService";
import DeleteModal from "../../../../../components/common/DeleteModal";
import Table from "../../../../../components/elements/table/Table";
import Button from "../../../../../components/form/Button";
import { setTitle } from "../../../../../helpers/MetaTag";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import {
  generateSlug,
  getPaginationRange,
  handleFilterChange,
  supportedLanguages,
  userLang,
} from "../../../../../helpers";
import TableControls from "../../../../../components/elements/TableControls";
import {
  featured_filter,
  status_filter,
} from "../../../../../components/constant/constant";
import useForm from "../../../../../hooks/useForm";
import Input from "../../../../../components/form/Input";
import Drawer from "../../../../../components/drawer/Drawer";
import Form from "../../../../../components/form/Form";
import FlexibleSelect from "../../../../../components/form/select/FlexibleSelect";
import LanguageTabs from "../../../../../components/elements/LanguageSwitcher";

const JobQualification = () => {
  setTitle("Jobs Qualification");
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [currentLang, setCurrentLang] = useState(
    localStorage.getItem("lang") || "en",
  );

  const handleLangChange = (lang) => {
    setCurrentLang(lang);
  };
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "ordering",
  });

  const validation = {
    title: { required: true, message: "Title is required" },
  };
  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    resetField,
    formValidate,
  } = useForm({
    validationRules: validation,
  });

  const { data, isLoading, updateData, isFetching } = useQuery({
    queryKey: ["qualification", filters, search],
    queryFn: async () => {
      const res = await taxonomyList({
        ...filters,
        ...(search && { keyword: search }),
        type: "qualification",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  //  status change handler =================>

  const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
    useMutation({
      mutationFn: async ({ id, status }) => {
        queryClient.setQueryData(
          ["qualification", filters, search],
          (state) => {
            if (!state) return;

            const updatedData = state?.data?.map((item) =>
              item?._id === id
                ? {
                    ...item,
                    status: item.status === "active" ? "inactive" : "active",
                  }
                : item,
            );

            return { ...state, data: updatedData };
          },
        );
        const res = await taxonomyStatusChange({ ids: [id], status });

        return { res, payload: { id, status } };
      },

      onSuccess: ({ res, payload }) => {
        if (res?.status === 200) {
          toast.success("Job qualification status change successfully.");
        } else {
          queryClient.setQueryData(
            ["qualification", filters, search],
            (state) => {
              if (!state) return;

              const revertedData = state?.data?.map((item) =>
                item?._id === payload.id
                  ? {
                      ...item,
                      status: item.status === "active" ? "inactive" : "active",
                    }
                  : item,
              );

              return { ...state, data: revertedData };
            },
          );

          toast.error(res?.message || "Failed to update status.");
        }
      },

      onError: (error) => {
        toast.error(
          error?.message || "Something went wrong while updating status.",
        );
        console.error("Status update error:", error);
      },
    });

  const tableHeadData = [
    {
      _id: 2,
      name: "Title",
      align: "left",
      isSort: false,
      onSort: (val) => {
        if (val === "DESC") {
          setFilters((pre) => ({ ...pre, sortQuery: "-title" }));
        } else {
          setFilters((pre) => ({ ...pre, sortQuery: "title" }));
        }
      },
    },

    {
      _id: 3,
      name: "Created At",
      align: "left",
      isSort: true,
      onSort: (val) => {
        if (val === "DESC") {
          setFilters((pre) => ({ ...pre, sortQuery: "-createdAt" }));
        } else {
          setFilters((pre) => ({ ...pre, sortQuery: "createdAt" }));
        }
      },
    },

    {
      _id: 4,
      name: "Status",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0 && val?.length === 1) {
          setFilters((pre) => ({
            ...pre,
            status: val?.map((item) => item?.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.status;
            return { ...pre };
          });
        }
      },
      filterData: status_filter || [],
    },

    {
      _id: 6,
      name: "Action",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.content?.[userLang]?.title || "-",
        },

        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.createdAt
            ? dayjs(item?.createdAt).format("DD MMM, YYYY")
            : "-",
        },

        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div
              className="flex gap-2 items-center cursor-pointer"
              onClick={() => {
                statusChangeHandler({
                  id: item?._id,
                  status: item?.status === "active" ? "inactive" : "active",
                });
              }}
            >
              <Badge
                type={item?.status === "active" ? "success" : "danger"}
                label={item?.status === "active" ? "Active" : "Inactive"}
              />
            </div>
          ),
        },

        {
          _id: 6,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: 1,
              name: "Edit",
              icon: "fa-regular fa-pen-to-square",
              onClick: () => {
                setModalOpen({ isOpen: true, data: item });
              },
            },
            {
              _id: 4,
              name: "Delete",
              icon: "fa-regular fa-trash-can",
              standout: true,
              onClick: () => {
                setDeleteModal({
                  isOpen: true,
                  data: item,
                });
              },
            },
          ],
        },
      ],
    };
  });

  // add or edit Response =================>

  const {
    data: details,
    isLoading: detailsLoading,
    isFetching: detailsFetching,
  } = useQuery({
    queryKey: ["qualification", modalOpen?.data?._id],
    queryFn: async () => {
      const res = await taxonomyDetails({ id: modalOpen?.data?._id });
      return res?.data;
    },
    enabled: !!modalOpen?.data?._id,
  });

  useEffect(() => {
    if (details?._id) {
      setFieldsValue({
        ...details,
        content: {
          ...supportedLanguages.reduce((acc, lang) => {
            acc[lang.code] = {
              title: details?.content?.[lang.code]?.title || "",
              content: details?.content?.[lang.code]?.content || "",
              excerpt: details?.content?.[lang.code]?.excerpt || "",
              seo: details?.content?.[lang.code]?.seo || {},
            };
            return acc;
          }, {}),
        },
        title: details?.content?.[currentLang]?.title || "",
        range: details?.range,
        status: details?.status,
      });
    } else if (modalOpen?.isOpen && !modalOpen?.data) {
      resetField();
      setFieldsValue({
        range: { min: "", max: "", unit: "years" },
        status: "active",
      });
    }
  }, [details, modalOpen?.isOpen]);

  async function taxonomyManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          queryClient.setQueryData(["experience", filters, search], (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = [...stateData, res?.data];
              return { ...state, data: stateData };
            }
          });
          await queryClient.refetchQueries(["experience", filters, search]);
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }

      if (res?.status === 200) {
        queryClient.setQueryData(["experience", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = stateData?.map((item) =>
              item?._id === res?.data?._id ? { ...item, ...res?.data } : item,
            );
            return { ...state, data: stateData };
          }
        });
        await queryClient.refetchQueries(["experience", filters, search]);
      }

      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  const {
    mutate: onSubmit,
    error,
    isPending: isLoadingManage,
  } = useMutation({
    mutationFn: async (val) => {
      const payload = {
        type: "qualification",
        content: val?.content,
        lang: userLang,
        slug: `${generateSlug(val?.content?.[userLang]?.title.replace(/\s+/g, "-").toLowerCase(), Date.now())}`,
        status: val?.status || "active",
      };
      return await taxonomyAddEdit(
        details?._id
          ? {
              ...(details?._id && {
                id: details?._id,
                ...payload,
              }),
            }
          : {
              ...payload,
            },
      );
    },
    onSuccess: (res) => {
      taxonomyManageResponse(res);
      if (res?.status === 200 || res?.status === 201) {
        resetField();
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });

  // category delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await taxonomyDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return { res, payload: Array.isArray(payload) ? payload : [payload] };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        queryClient.setQueryData(
          ["qualification", filters, search],
          (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = (stateData || [])?.filter(
                (item) => !payload?.includes(item?._id),
              );
              return { ...state, data: stateData };
            }
          },
        );
        toast.success("Qualification deleted successfully.");

        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["qualification", filters, search]);
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
      toast.error(
        error?.message || "Something went wrong while delete qualification.",
      );
    },
  });

  return (
    <div>
      <div className="space-y-4 w-full flex flex-col pb-10">
        <div className="space-y-8">
          <div className="flex items-center gap-3">
            <TableControls
              limit={filters?.limit}
              totalDocs={data?.metadata?.totalDocs}
              onLimitChange={(val) =>
                handleFilterChange("limit", val, setFilters)
              }
              onSearch={(val) => {
                setKeyword(val);
                handleFilterChange("page", 1, setFilters);
              }}
            />
            <div className="relative ml-auto">
              <Button
                buttonClasses={""}
                buttonLabel={"Add Qualification"}
                buttonIcon={"fa-regular fa-plus"}
                buttonIconPosition="left"
                buttonLabelClasses={"font-medium !text-sm"}
                buttonFunction={() =>
                  setModalOpen({ isOpen: true, data: null })
                }
              />
            </div>
          </div>
        </div>

        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          totalDataCount={data?.metadata?.totalDocs}
          isLoder={isLoading}
          pageCount={data?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          handleOrdering={(val) => {
            console.log(val);
          }}
          isFetching={isFetching}
          isOrderingDisabled={true}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          selectable
          tableActionDropdownContainer={"!w-48"}
          showingData={getPaginationRange(
            filters?.page,
            filters?.limit,
            data?.metadata?.totalDocs,
          )}
        />

        <Drawer
          open={modalOpen?.isOpen}
          onClose={() => {
            setModalOpen({ isOpen: false, data: null });
            resetField();
          }}
          actualSize={"xl"}
          size={"xl3"}
          title={details?._id ? "Update Qualifications" : "Add Qualifications"}
          subtitle=""
          drawerBodyClass="p-0 max-h-[calc(100%-90px)]"
        >
          <div className="h-full overflow-y-auto">
            <Form
              onSubmit={onSubmit}
              handleSubmit={handleSubmit}
              loading={isLoading}
            >
              <div className="grid grid-cols-1 m-7 gap-3">
                {/* <LanguageTabs languages={supportedLanguages} defaultLang="en" value={currentLang} onChange={handleLangChange} /> */}
                <Input
                  label={"Qualification"}
                  inputType={"title"}
                  inputPlaceholder="Add qualification"
                  inputName={"title"}
                  value={values?.content?.[currentLang]?.title || ""}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setFieldsValue({
                      ...values,
                      title: newValue,
                      content: {
                        ...values.content,
                        [currentLang]: {
                          ...values.content?.[currentLang],
                          title: newValue,
                        },
                      },
                    });
                  }}
                  {...formValidate(errors, "title")}
                />
              </div>
              <div className="grid grid-cols-1 m-7 gap-3">
                <FlexibleSelect
                  label={"Status"}
                  placeholder={"Choose status"}
                  onChange={(e) =>
                    handleChange({ target: { name: "status", value: e } })
                  }
                  selectedValue={values?.status}
                  dropdownData={[
                    { name: "Active", value: "active" },
                    { name: "Inactive", value: "inactive" },
                  ]}
                  isDisabled={details?._id}
                  {...formValidate(errors, "status")}
                />
              </div>

              <div className="pt-3 pb-6 flex justify-end m-7">
                <Button
                  buttonType="submit"
                  buttonClasses={"min-w-36"}
                  buttonLabel={details?._id ? "Update" : "Save"}
                  {...((isLoadingManage || isLoading || detailsLoading) && {
                    loading: true,
                    buttonIconPosition: "left",
                    isDisable: true,
                  })}
                />
              </div>
            </Form>
          </div>
        </Drawer>
        <DeleteModal
          open={deleteModal?.isOpen}
          loading={deletePending}
          onClose={() =>
            setDeleteModal((pre) => ({ data: null, isOpen: false }))
          }
          deleteHandler={() => {
            deleteHandler(deleteModal?.data?._id);
          }}
        />
      </div>
    </div>
  );
};

export default JobQualification;
